import {
  Box,
  Button,
  Grid,
  HStack,
  Icon,
  IconButton,
  Stack,
  StackDivider,
  Text,
  VStack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  useDisclosure,
  Tooltip
} from '@chakra-ui/react';
import React from 'react';
import { MdEast, MdInfoOutline } from 'react-icons/md';
import { getOrderStatusColor } from '@utils';
import { BsFillFlagFill, BsPlusCircle } from 'react-icons/bs';
import CopyableText from '@components/CopyableText';
import { ORDER_RESOURCE } from '@constants';
import orderStatusTooltipData from '@constants/orderStatusTooltipData.json';


const OrderCard = ({
   onOrderDetailsClick,
   orderData,
   show,
   onFlagButtonClick,
   isSidebar = false,
   orderResource = ORDER_RESOURCE.APP_SERVER
 }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const isRemittance = orderData?.product === 'REMITTANCE';

  // Find status tooltip info
  const statusTooltipInfo = orderStatusTooltipData.find(
    (item) => orderData?.orderSubStatus === item.status
  );
  const isBBPS = orderData?.product === 'BBPS';

  // Get bills count for BBPS orders
  const billsCount = isBBPS ? orderData?.additionalDetails?.bills?.length || 0 : 0;

  // Only show compliance flag for REMITTANCE orders
  const shouldShowComplianceFlag = isRemittance && orderData?.metadata?.complianceFlagged;
  const shouldShowAddFlag = isRemittance && !orderData?.metadata?.complianceFlagged;

  // Get order type specific label
  const getOrderTypeLabel = () => {
    if (isRemittance) return 'Order ID:';
    if (isBBPS) return 'Order ID:';
    return 'Order ID:';
  };

  // Get recipient/bills display
  const getRecipientDisplay = () => {
    if (isRemittance) {
      return (
        <Stack
          direction={{ base: 'row', md: 'column' }}
          justifyContent={'space-between'}
        >
          <Text fontSize={'sm'} color={'gray'}>
            Recipient
          </Text>
          <Text fontSize={'sm'} color={'white'}>
            {orderData.beneficiaryName || '-'}
          </Text>
        </Stack>
      );
    }

    if (isBBPS) {
      return (
        <Stack
          direction={{ base: 'row', md: 'column' }}
          justifyContent={'space-between'}
        >
          <HStack>
            <Text fontSize={'sm'} color={'gray'}>
              Bills
            </Text>
            {billsCount > 0 && (
              <Popover
                isOpen={isOpen}
                onOpen={onOpen}
                onClose={onClose}
                placement="top"
              >
                <PopoverTrigger>
                  <IconButton
                    size="xs"
                    variant="ghost"
                    color="gray.400"
                    icon={<MdInfoOutline />}
                    _hover={{ color: 'white' }}
                  />
                </PopoverTrigger>
                <PopoverContent bg="gray.800" borderColor="gray.600">
                  <PopoverArrow bg="gray.800" />
                  <PopoverCloseButton />
                  <PopoverHeader color="white" fontWeight="bold">
                    Bills Details
                  </PopoverHeader>
                  <PopoverBody>
                    <VStack align="start" spacing={2}>
                      {orderData?.additionalDetails?.bills?.map((bill, index) => (
                        <Box key={index} p={2} bg="gray.700" rounded="md" w="100%">
                          <Text fontSize="xs" color="gray.300">Category:</Text>
                          <Text fontSize="sm" color="white">{bill.category}</Text>
                          <Text fontSize="xs" color="gray.300" mt={1}>Detail:</Text>
                          <Text fontSize="sm" color="white">{bill.detail}</Text>
                          {bill.amount && (
                            <>
                              <Text fontSize="xs" color="gray.300" mt={1}>Amount:</Text>
                              <Text fontSize="sm" color="white">
                                {bill.amount.value} {bill.amount.currency}
                              </Text>
                            </>
                          )}
                        </Box>
                      ))}
                    </VStack>
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            )}
          </HStack>
          <Text fontSize={'sm'} color={'white'}>
            {billsCount} {billsCount === 1 ? 'Bill' : 'Bills'}
          </Text>
        </Stack>
      );
    }

    return null;
  };

  // Get amount display labels
  const getAmountLabels = () => {
    if (isRemittance) {
      return {
        debit: 'Debit Amount',
        credit: 'Credit amount'
      };
    }
    if (isBBPS) {
      return {
        debit: 'Payment Amount',
        credit: '' // BBPS might not have credit amount
      };
    }
    return {
      debit: 'Amount',
      credit: 'Amount'
    };
  };

  const amountLabels = getAmountLabels();

  return (
    <Box position={'relative'}>
      {/* Compliance flag - only for REMITTANCE orders */}
      {shouldShowComplianceFlag && (
        <IconButton
          position={'absolute'}
          rounded={'full'}
          top={-2}
          right={-2}
          bg={'altGray.500'}
          color={'red.500'}
          _hover={{
            background: 'red.500',
            color: 'altGray.500'
          }}
          size={'sm'}
          fontSize={'0.9rem'}
          fontWeight={400}
          icon={<BsFillFlagFill />}
          onClick={onFlagButtonClick}
        />
      )}

      {/* Add flag - only for REMITTANCE orders */}
      {shouldShowAddFlag && (
        <IconButton
          position={'absolute'}
          rounded={'full'}
          top={-2}
          right={-2}
          bg={'altGray.500'}
          color={'brand.200'}
          _hover={{
            background: 'brand.200',
            color: 'altGray.500'
          }}
          size={'sm'}
          fontSize={'0.9rem'}
          fontWeight={400}
          icon={<BsPlusCircle />}
          onClick={onFlagButtonClick}
        />
      )}

      <VStack
        bg="backGround"
        borderRadius="12px"
        divider={
          <StackDivider style={{ margin: '0' }} borderColor="whiteAlpha.300" />
        }
      >
        <Grid
          gridTemplateColumns={
            isSidebar
              ? {
                base: 'repeat(1, auto)'
              }
              : {
                base: 'repeat(2, auto)',
                md: 'repeat(3, auto)'
              }
          }
          rowGap={2}
          w={'90%'}
          maxW={'100%'}
          justifyContent="space-between"
          m={'1rem'}
        >
          <HStack>
            <Text fontSize={{ base: 'sm', sm: 'md' }} color="gray">
              {getOrderTypeLabel()}
            </Text>
            <CopyableText
              title={'Order ID'}
              fontSize={{ base: 'sm', sm: 'md' }}
              color="white"
            >
              {orderData.orderId}
            </CopyableText>
          </HStack>
          <HStack>
            <Icon
              viewBox="0 0 200 200"
              color={getOrderStatusColor(orderData.orderStatus)}
              w="8px"
            >
              <path
                fill="currentColor"
                d="M 100, 100 m -75, 0 a 75,75 0 1,0 150,0 a 75,75 0 1,0 -150,0"
              />
            </Icon>
            <Text fontSize={{ base: 'sm', sm: 'md' }} color="white">
              {orderData.orderStatus}
            </Text>
            {statusTooltipInfo && isRemittance && (
              <Tooltip
                label={
                  <Box p={1}>
                    <Text fontWeight="bold" mb={1}>User Order Status:</Text>
                    <Text mb={2}>{orderData.additionalDetails?.userOrderStatus}</Text>
                    <Text fontWeight="bold" mb={1}>Order SubStatus:</Text>
                    <Text mb={2}>{orderData.orderSubStatus}</Text>
                    <Text fontWeight="bold" mb={1}>Meaning:</Text>
                    <Text mb={2}>{statusTooltipInfo.meaning}</Text>
                    <Text fontWeight="bold" mb={1}>Action:</Text>
                    <Text>{statusTooltipInfo.action}</Text>
                  </Box>
                }
                placement="top"
                hasArrow
                bg="gray.700"
                color="white"
                borderRadius="md"
                maxW="300px"
              >
                <Box as="span" cursor="pointer" display="inline-flex" alignItems="center">
                  <Icon as={MdInfoOutline} color="gray.400" _hover={{ color: 'white' }} />
                </Box>
              </Tooltip>
            )}
          </HStack>
          <Stack direction={{ base: 'column', sm: 'row' }}>
            <Text fontSize={{ base: 'sm', sm: 'md' }} color="gray">
              Created on:
            </Text>
            <Text fontSize={{ base: 'sm', sm: 'md' }} color="white">
              {orderData.createdAt}
            </Text>
          </Stack>
        </Grid>

        <Grid
          gridTemplateColumns={
            isSidebar
              ? {
                base: '1fr'
              }
              : {
                base: '1fr',
                md: `1fr ${isRemittance && amountLabels.credit ? '1fr' : ''} ${show ? '1.2fr' : ''} 1.2fr auto`
              }
          }
          gap={2}
          alignItems={'flex-start'}
          w={'90%'}
          m={'1rem'}
          textAlign={{ base: 'end', md: 'start' }}
        >
          {/* Debit/Payment Amount */}
          <Stack
            direction={{ base: 'row', md: 'column' }}
            justifyContent={'space-between'}
          >
            <Text fontSize={'sm'} color={'gray'}>
              {amountLabels.debit}
            </Text>
            <Text fontSize={'sm'} color={'white'}>
              {`${orderData.amount} ${orderData.currencyFrom}`}
            </Text>
          </Stack>

          {/* Credit Amount - only for REMITTANCE */}
          {isRemittance && amountLabels.credit && (
            <Stack
              direction={{ base: 'row', md: 'column' }}
              justifyContent={'space-between'}
            >
              <Text fontSize={'sm'} color={'gray'}>
                {amountLabels.credit}
              </Text>
              <Text fontSize={'sm'} color={'white'}>
                {`${orderData.receiveAmnt} ${orderData.currencyTo}`}
              </Text>
            </Stack>
          )}
          

          {/* Dynamic show field */}
          {show && (
            <Stack
              direction={{ base: 'row', md: 'column' }}
              justifyContent={'space-between'}
            >
              <Text fontSize={'sm'} color={'gray'}>
                {show
                  .replace(/([A-Z])/g, ' $1')
                  .split(' ')
                  .map((word) => word.slice(0, 1).toUpperCase() + word.slice(1))
                  .join(' ')}
              </Text>
              <Text fontSize={'sm'} color={'white'}>
                {orderData[show] || '-'}
              </Text>
            </Stack>
          )}
          <Stack direction={{ base: 'column', sm: 'row' }}>
            <Text fontSize={{ base: 'sm', sm: 'md' }} color="gray">
              Source Name:
            </Text>
            <Text fontSize={{ base: 'sm', sm: 'md' }} color="white">
              {orderData.user}
            </Text>
          </Stack>

          {/* Recipient/Bills Display */}
          {getRecipientDisplay()}

          {/* Order Details Button */}
          <Button
            mt={{ base: 2, md: 0 }}
            ml={{ base: 0, md: 1 }}
            rightIcon={<MdEast color="black" />}
            type="submit"
            rounded="lg"
            color={'black'}
            colorScheme="brand"
            alignSelf={{ base: 'center', md: 'center' }}
            px={5}
            onClick={() => {
              onOrderDetailsClick(orderData.orderId, orderResource);
            }}
          >
            Order details
          </Button>
        </Grid>
      </VStack>
    </Box>
  );
};

export default OrderCard;